import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { CSVLink } from 'react-csv';

import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Grid from '@mui/material/Grid';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers';

import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/CloudDownload';

import CaseAccordion from '../../CaseAccordion';
import NoAccess from '../../NoAccess';
import Button from '../../../../../shared/Button';
import Input from '../../../../../shared/Input';

import blockIcon from '../../../../../../assets/block_icon.svg';
import calendarIcon from '../../../../../../assets/calendar_icon.svg';

import { setActiveTab, updateCase } from '../../../../../../actions/casesActions';
import { caseConstants } from '../../../../../../constants';

const { fileStatuses, caseNotifications, statuses } = caseConstants;

const exportHeaders = [
  'Patient ID:',
  'Surgery Date:',
  'Required Date:',
  'Category:',
  'SKU:',
  'Description:',
  'QTY:',
];

const OsteotomyStep7 = (props) => {
  const {
    onClose,
    activeCase,
    updateCase,
    goToFiles,
    blocks,
    userId,
  } = props;

  const [files, setFiles] = useState(blocks);
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(moment());
  const [exportData, setExportData] = useState([]);

  const preparedDataForExport = (data = []) => {
    const preparedData = [];

    const caseId = activeCase?.id;
    const surgeryDate = moment(activeCase?.date).format('DD/MM/YYYY');
    const requiredDate = moment(activeCase?.requiredDate).format('DD/MM/YYYY');

    preparedData.push([caseId, surgeryDate, requiredDate]);

    data.forEach((item) => {
      const product = item?.product?.split(' — ') || [];
      preparedData.push([
        '',
        '',
        '',
        item?.category || '',
        product[0] || '',
        product[1] || '',
        item?.amount || '',
      ]);
    });

    return preparedData;
  };

  useEffect(() => {
    setExportData(preparedDataForExport(activeCase?.manufacturing));
  }, [activeCase?.manufacturing]);

  const handleSubmit = () => {
    if (files.some((f) => f.status === fileStatuses.REJECTED) && !notes?.length) return;

    const now = moment().format();
    const activity = [...activeCase.activity];
    const caseFiles = [...activeCase.files];

    let rejected = false;

    if (files.some((f) => f.status === fileStatuses.REJECTED)) {
      activity.unshift({ ...caseNotifications[7].reject, date: now, userId });
      rejected = true;
    } else {
      activity.unshift({ ...caseNotifications[7].success, date: now, userId });
    }

    files.forEach((file) => {
      const caseFile = caseFiles.find((f) => f.id === file.id);
      if (caseFile) caseFile.status = file.status;
    });

    updateCase(
      {
        ...activeCase,
        files: caseFiles,
        step: rejected ? 5 : 8,
        activity,
        completionDates: { ...activeCase.completionDates, step7: now },
        completedBy: { ...activeCase.completedBy, step7: userId },
        notes: { ...activeCase.notes, step7: notes },
        dispatchInfo: { targetedDispatchDate: date.format() },
        status: rejected ? statuses.rejected : statuses.inProgress,
      },
      rejected
    ).then(() => onClose());
  };

  const handleChange = (id, status) => {
    setFiles((prev) =>
      prev.map((file) => (file.id === id ? { ...file, status } : file))
    );
  };

  const rejected = files.some((f) => f.status === fileStatuses.REJECTED);
  const disabled = rejected && !notes?.length;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="case-step-container">
        <div className="case-step-content">
          <div className="case-step-row">
            <div className="column">
              <div className="modal-item-circle">7</div>
            </div>

            <div className="modal-item-content">
              <div>Manufacturing Approval</div>

              {activeCase.access ? (
                <Fragment>
                  {!!files?.length && (
                    <div className="modal-item-block">
                      <div className="block-header">Manufacturing files</div>

                      {files.map((file) => (
                        <div key={file.id} className="block-file-list">
                          <div className="d-flex pointer" onClick={goToFiles}>
                            <img src={blockIcon} alt="block-icon" />
                            {file.name}
                          </div>

                          <RadioGroup
                            row
                            value={file.status}
                            onChange={(e) => handleChange(file.id, e.target.value)}
                          >
                            <FormControlLabel
                              value={fileStatuses.APPROVED}
                              control={<Radio color="primary" />}
                              label="Approve"
                            />
                            <FormControlLabel
                              value={fileStatuses.REJECTED}
                              control={<Radio color="primary" />}
                              label="Reject"
                            />
                          </RadioGroup>
                        </div>
                      ))}
                    </div>
                  )}

                  {rejected && (
                    <Input
                      fullWidth
                      multiline
                      label="Notes"
                      placeholder="Add reason for reject here"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  )}

                  {!!activeCase?.manufacturing?.length && (
                    <div className="manufacturing-list-container">
                      <div className="manufacturing-list-header">
                        <div className="secondary uppercase font-size-sm">
                          PSI Manufacturing
                        </div>

                        <CSVLink
                          data={exportData}
                          headers={exportHeaders}
                          filename={`${activeCase.patientLastName}.csv`}
                        >
                          <IconButton>
                            <DownloadIcon color="primary" />
                          </IconButton>
                        </CSVLink>
                      </div>

                      <div className="manufacturing-list">
                        {activeCase.manufacturing.map((item, idx) => (
                          <Grid key={idx} container className="underline p-t-md p-b-md">
                            <Grid size={{ xs: 3 }}>{item.category}</Grid>
                            <Grid size={{ xs: 8 }}>{item.product}</Grid>
                            <Grid size={{ xs: 1}}>{item.amount}</Grid>
                          </Grid>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="m-t-lg d-flex align-start">
                    <div className="flex-1">
                      <div className="secondary font-size-sm m-b-sm">
                        TARGETED SURGERY DATE
                      </div>
                      <div className="d-flex">
                        <img src={calendarIcon} alt="calendar-icon" height={15} style={{ marginRight: '8px' }} />
                        {activeCase.date
                          ? moment(activeCase.date).format('DD/MM/YYYY')
                          : 'TBC'}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="secondary font-size-sm">
                        TARGETED DISPATCH DATE
                      </div>

                      <DatePicker
                        desktopModeMediaQuery="(min-width: 0px)"
                        value={date}
                        onChange={(newDate) => setDate(newDate)}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: { variant: 'standard' },
                        }}
                      />
                    </div>
                  </div>
                </Fragment>
              ) : (
                <NoAccess />
              )}
            </div>
          </div>

          <CaseAccordion currentStep={7} />
        </div>

        <div className="case-modal-actions">
          <div className="secondary pointer" onClick={onClose}>
            Close
          </div>
          <Button
            text="Submit"
            color="primary"
            onClick={handleSubmit}
            disabled={!activeCase.access || disabled}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};

OsteotomyStep7.propTypes = {
  activeCase: PropTypes.object,
  blocks: PropTypes.array,
  onClose: PropTypes.func,
  updateCase: PropTypes.func,
  goToFiles: PropTypes.func,
};

const mapStateToProps = (state) => {
  const blocks = JSON.parse(
    JSON.stringify(
      state.cases.activeCase.files?.filter((file) => file.type === 'block')
    )
  );

  blocks.forEach((file) => {
    file.status = fileStatuses.APPROVED;
  });

  return {
    activeCase: state.cases.activeCase,
    blocks,
    userId: state.user.currentUser.uid,
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateCase: (newCase, rejected) =>
    dispatch(updateCase(newCase, 7, null, rejected)),
  goToFiles: () => dispatch(setActiveTab(2)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OsteotomyStep7);
